import AppFormContainer from '@nxsys/components/AppFormContainer';
import { EeErPensionMethodEnum } from '@nxsys/constants/AppEnums';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import { getTypeOfValueOptions } from '@nxsys/constants/AppConstants';
import {
  useCreateEmployeeIrelandPrivatePension,
  useGetAvailableCompanyPensionSettings,
} from '@nxsys/hooks/api/employee/useEmployeeApi';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IPrivateIrelandPension } from '@nxsys/types/models/pension';
import { showCurrencyByCountry } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { Col, Form, Row } from 'antd';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';
import { privatePensionValueRule } from '@nxsys/utils/formFieldValidation';

type Props = {
  isOpen: boolean;
  toggle: () => void;
  subcontractorId: string;
  onSuccess: () => void;
};

const EmployeePrivatePensionAddModal: React.FC<Props> = ({
  isOpen,
  toggle,
  subcontractorId,
  onSuccess,
}) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm();
  const erPensionType = Form.useWatch('erPensionType', form);
  const eePensionType = Form.useWatch('eePensionType', form);
  const avcPensionType = Form.useWatch('avcPensionType', form);
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const pensionTypeOptions = getTypeOfValueOptions(
    showCurrencyByCountry(payrollCountry),
  );

  const {
    value: availableSettings,
    loading: loadingSettings,
    run: fetchAvailable,
  } = useGetAvailableCompanyPensionSettings();

  const { run: createSetting, loading: creating } =
    useCreateEmployeeIrelandPrivatePension();

  useEffect(() => {
    if (isOpen) {
      fetchAvailable(subcontractorId);
      form.resetFields();
    }
  }, [isOpen]);

  const companySettingOptions = useMemo(
    () =>
      (availableSettings ?? []).map((s: IPrivateIrelandPension) => ({
        label: `${s.type} - ${s.name || s.reference || ''}`,
        value: s.id,
      })),
    [availableSettings],
  );

  const handleSubmit = async (values: any) => {
    const companySettingId = values.companyPensionSettingId?.value ?? values.companyPensionSettingId;
    const res = await createSetting(subcontractorId, {
      companyPensionSettingId: companySettingId,
      eePensionValue: Number(values.eePensionValue) || 0,
      eePensionType: values.eePensionType?.value || 'PERCENTAGE',
      erPensionValue: Number(values.erPensionValue) || 0,
      erPensionType: values.erPensionType?.value || 'PERCENTAGE',
      avcPensionValue: Number(values.avcPensionValue) || 0,
      avcPensionType: values.avcPensionType?.value || 'PERCENTAGE',
    });
    if (res) {
      showMessage({ type: 'success', content: messages['common.addSuccess'] });
      toggle();
      onSuccess();
    }
  };

  return (
    <AppModalContainer
      title={messages['common.addPensionScheme'] ?? 'Add Pension Scheme'}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        { name: messages['common.cancel'], variant: 'outlined', onClick: toggle },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          loading: creating,
        },
      ]}
    >
      <AppFormContainer
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        initialValues={{
          eePensionType: pensionTypeOptions[0],
          eePensionValue: 0,
          erPensionType: pensionTypeOptions[0],
          erPensionValue: 0,
          avcPensionType: pensionTypeOptions[0],
          avcPensionValue: 0,
        }}
      >
        <Form.Item
          label={messages['common.pensionScheme'] ?? 'Pension Scheme'}
          name="companyPensionSettingId"
          rules={[{ required: true, message: 'Please select a pension scheme' }]}
        >
          <AppSelect
            placeholder="Select company pension scheme"
            options={companySettingOptions}
            loading={loadingSettings}
            allowClear
          />
        </Form.Item>
        <Row gutter={24}>
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.employeeContribution'] ?? 'EE Contribution'}>
              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item name="eePensionType" noStyle>
                    <AppSelect options={pensionTypeOptions} className="w-full !min-w-0" />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item
                    name="eePensionValue"
                    noStyle
                    dependencies={['eePensionType']}
                    rules={[privatePensionValueRule('eePensionType', messages)]}
                  >
                    {eePensionType?.value === EeErPensionMethodEnum.AMOUNT ? (
                      <AppCurrencyInput placeholder="0" />
                    ) : (
                      <AppRateInput placeholder="0" />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
          </Col>
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.employerContribution'] ?? 'ER Contribution'}>
              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item name="erPensionType" noStyle>
                    <AppSelect options={pensionTypeOptions} className="w-full !min-w-0" />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item
                    name="erPensionValue"
                    noStyle
                    dependencies={['erPensionType']}
                    rules={[privatePensionValueRule('erPensionType', messages)]}
                  >
                    {erPensionType?.value === EeErPensionMethodEnum.AMOUNT ? (
                      <AppCurrencyInput placeholder="0" />
                    ) : (
                      <AppRateInput placeholder="0" />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
          </Col>
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.avcContribution'] ?? 'AVC Contribution'}>
              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item name="avcPensionType" noStyle>
                    <AppSelect options={pensionTypeOptions} className="w-full !min-w-0" />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item
                    name="avcPensionValue"
                    noStyle
                    dependencies={['avcPensionType']}
                    rules={[privatePensionValueRule('avcPensionType', messages)]}
                  >
                    {avcPensionType?.value === EeErPensionMethodEnum.AMOUNT ? (
                      <AppCurrencyInput placeholder="0" />
                    ) : (
                      <AppRateInput placeholder="0" />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
          </Col>
        </Row>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default EmployeePrivatePensionAddModal;
